/**
 * A band has at most one stream source: `{ source, channel_id }` served by
 * `GET /public/bands/{id}/stream/source` (404 when none) and written with
 * `PUT` / `DELETE /bands/{id}/stream/source`. Listeners never read it — the
 * player URL comes back as `embedUrl` on `/public/bands/{id}/stream/status`.
 *
 * The validation below mirrors the backend's NormalizeChannelID and
 * ParseYouTubeChannelRef (streaming/embed.go) so a band member gets the same
 * "what to paste instead" message before the request is made, and so the
 * PUT's 400 keeps one meaning for a handle (see below). Keep the regexes in
 * step when either side changes.
 *
 * For YouTube a band member can type the channel's @handle (what YouTube
 * shows everywhere and what people know) as well as a channel ID. YouTube's
 * live_stream embed cannot use a handle, so the backend resolves it to the
 * channel ID with the YouTube Data API before storing anything (ENG-348,
 * bands/youtube_handles.go): the client sends the handle as `channel_id` as
 * typed and the PUT answers 400 when no channel has it, 429 when the member
 * has spent their hourly lookup budget and 503 when lookups are unavailable.
 * That 503 covers two situations the member must be told apart: YouTube
 * unreachable or over quota for the moment (Retry-After: 60, retrying helps),
 * and the server's own key missing, rejected or its daily quota spent (no
 * Retry-After; retrying cannot help until an administrator acts). The body is
 * plain text written for the member and says which; fetchWrapper passes it on
 * as `message`, and streamSourceSaveError shows it for a 503. Without it the
 * fallback wording promises nothing about retrying: ENG-360 was production
 * telling members to retry a key failure that no retry could fix.
 *
 * The browser never talks to Google itself and ships no API key: ENG-349 did
 * both, and every deploy then hinged on a browser key being present and
 * accepted for the site's referrers (ENG-360). ENG-364 removed that. It also
 * means a backend without ENG-348 answers a handle with a 500, which is why
 * every handle failure offers the channel ID as the way round.
 */

import { englishT } from '../locales/index.js';

/**
 * Platforms a band member can pick. `songshake` is first-party and has no
 * player yet.
 *
 * Every piece of copy is a catalog key (`labelKey`, `descriptionKey`, …) for
 * components to pass through `t()`; the English `label`, `description`,
 * `channelLabel` and `placeholder` stay for callers without a `t` (ENG-386).
 */
export const STREAM_SOURCES = [
  {
    value: 'twitch',
    labelKey: 'streamSource.providers.twitch.label',
    descriptionKey: 'streamSource.providers.twitch.description',
    channelLabelKey: 'streamSource.providers.twitch.channelLabel',
    placeholderKey: 'streamSource.providers.twitch.placeholder',
  },
  {
    value: 'youtube',
    labelKey: 'streamSource.providers.youtube.label',
    descriptionKey: 'streamSource.providers.youtube.description',
    channelLabelKey: 'streamSource.providers.youtube.channelLabel',
    placeholderKey: 'streamSource.providers.youtube.placeholder',
  },
].map((entry) => ({
  ...entry,
  label: englishT(entry.labelKey),
  description: englishT(entry.descriptionKey),
  channelLabel: englishT(entry.channelLabelKey),
  placeholder: englishT(entry.placeholderKey),
}));

/** Sources the backend accepts but the panel cannot author (no platform to pick). */
const READ_ONLY_SOURCE_LABEL_KEYS = { songshake: 'streamSource.providers.songshake' };

const TWITCH_LOGIN_PATTERN = /^[A-Za-z0-9_]{1,25}$/;
const YOUTUBE_CHANNEL_ID_PATTERN = /^UC[A-Za-z0-9_-]{22}$/;
// YouTube handles: 3-30 letters, digits, underscores, hyphens and periods
// (case-insensitive), at least one of them a letter or digit.
const YOUTUBE_HANDLE_PATTERN = /^(?=.*[A-Za-z0-9])[A-Za-z0-9_.-]{3,30}$/;

/*
 * The member-facing messages live in the `streamSource.errors` catalog
 * namespace (ENG-386); the English constants below are read from it so the
 * two can never drift. Every handle-failure message ends by saying how to
 * get the channel ID by hand (YouTube Studio → Settings → Channel → Advanced
 * settings): a channel ID needs no lookup, so it is always accepted, whatever
 * the server's lookup budget or key situation. Each message is written out
 * whole rather than assembled from that clause, so a translation can order
 * the sentence its own way.
 */
export const TWITCH_CHANNEL_ERROR = englishT('streamSource.errors.twitchChannel');
export const YOUTUBE_CHANNEL_ERROR = englishT('streamSource.errors.youtubeChannel');

/** The PUT answered 400 for a handle: YouTube has no channel with it. */
export const youtubeHandleNotFoundError = (handle, t = englishT) =>
  t('streamSource.errors.handleNotFound', { handle });
/** 429: the member is over the server's per-user budget of handle lookups (20 an hour). */
export const YOUTUBE_HANDLE_LOOKUP_LIMIT_ERROR = englishT('streamSource.errors.handleLookupLimit');
/**
 * 503 and the server's own words did not come through (an intermediary
 * replaced the body): it cannot look handles up, for the moment or until an
 * administrator fixes its API key, and only its body says which. So this
 * neither promises that retrying will help nor says that it will not; it
 * offers the one thing that works either way.
 */
export const YOUTUBE_HANDLE_LOOKUP_UNAVAILABLE_ERROR = englishT('streamSource.errors.handleLookupUnavailable');
/**
 * Any other failure with a handle: an outage, or a backend that does not
 * resolve handles yet (before ENG-348 it answers a handle with a 500). Either
 * way the channel ID gets the source set now.
 */
export const YOUTUBE_HANDLE_SAVE_FAILED_ERROR = englishT('streamSource.errors.handleSaveFailed');

/** True when `source` is one of the platforms the setup panel can author. */
export const isSelectableStreamSource = (source) =>
  STREAM_SOURCES.some((entry) => entry.value === source);

/** Display name of a source in the given locale (English without a `t`); unknown sources pass through. */
export const streamSourceLabel = (source, t = englishT) => {
  const labelKey = STREAM_SOURCES.find((entry) => entry.value === source)?.labelKey ?? READ_ONLY_SOURCE_LABEL_KEYS[source];
  return labelKey ? t(labelKey) : source ?? '';
};

/**
 * Only https URLs with a host may be framed. Listener pages embed whatever the
 * server hands back for performers the listener did not choose, so an
 * unexpected scheme (javascript:, data:, …) or a malformed value must fall
 * through to the placeholder rather than into an iframe.
 */
export const isSafeEmbedUrl = (url) => {
  if (typeof url !== 'string' || url.trim() === '') return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname !== '';
  } catch {
    return false;
  }
};

/**
 * `allow` lists for the player iframes. The stream page (VideoPlayer) and the
 * home dashboard cards (HomeStreamEmbed) frame the same providers, so they
 * share these rather than each hand-maintaining a copy: the copies had already
 * drifted once, with the home frames missing `fullscreen` (ENG-347).
 *
 * Playback controls, including fullscreen, are owned entirely by the embedded
 * player, so it must be allowed to go fullscreen. `fullscreen` is declared in
 * `allow` explicitly, for parity across the frames and so the grant does not
 * lean on the legacy attribute; `allowfullscreen` is kept alongside as a hedge
 * for engines without `allow` support. Per Permissions Policy, when both are
 * present the `allow` token wins and `allowfullscreen` has no effect (Chromium
 * says so on the console once per frame: "Allow attribute will take precedence
 * over 'allowfullscreen'" - expected, not a fault). Without the token,
 * `allowfullscreen` alone would also have permitted fullscreen, for every
 * origin (`*`); a bare `fullscreen` token grants it to the frame's `src`
 * origin only, which is deliberate. The backend emits only Twitch and YouTube
 * players (STREAM_SOURCES; `songshake` has none), and both render the video
 * surface in the embed's own document. Widen to `fullscreen *` if a provider
 * that nests its player in a cross-origin frame is ever added.
 *
 * `EMBED_ALLOW` (server-built `embedUrl`) is YouTube's official embed snippet
 * plus `fullscreen`: `accelerometer`/`gyroscope` serve 360° video and
 * `clipboard-write` the player's share menu. The Twitch channel fallback needs
 * only what its player uses.
 */
export const EMBED_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture';
export const TWITCH_EMBED_ALLOW = 'autoplay; fullscreen';

/**
 * Each provider's own "start muted" player parameter, keyed by the host its
 * player URL is served from (a subdomain of it counts, e.g. player.twitch.tv,
 * www.youtube-nocookie.com).
 */
const MUTE_PARAMS = [
  { host: 'twitch.tv', name: 'muted', value: 'true' },
  { host: 'youtube.com', name: 'mute', value: '1' },
  { host: 'youtube-nocookie.com', name: 'mute', value: '1' },
];

/**
 * `url` with the provider's start-muted parameter set, for a frame that shows
 * the band its own stream: the performer's dashboard confidence monitor must
 * never play the room's audio back into the room several seconds late
 * (HomeStreamEmbed does the same for its Twitch cards). Autoplay stays on —
 * browsers allow muted autoplay, and a preview that has to be clicked into
 * life is not a preview. Unknown hosts and unparsable strings come back as
 * given; whether the URL is safe to frame at all is isSafeEmbedUrl's call.
 */
export const mutedEmbedUrl = (url) => {
  if (typeof url !== 'string') return url;
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    const param = MUTE_PARAMS.find(({ host }) => hostname === host || hostname.endsWith(`.${host}`));
    if (!param) return url;
    parsed.searchParams.set(param.name, param.value);
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Path of `raw` (no leading slash, percent-decoded so a copied
 * `/%40the_band` reads as `/@the_band`) when it is a URL on `host` or a
 * subdomain of it; otherwise null.
 */
const urlPath = (raw, host) => {
  try {
    const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
    const hostname = parsed.hostname.toLowerCase();
    if (hostname !== host && !hostname.endsWith(`.${host}`)) return null;
    return decodeURIComponent(parsed.pathname).replace(/^\//, '');
  } catch {
    return null;
  }
};

/**
 * Turn what the band member typed into the channel identifier the backend
 * stores, or say what is expected.
 *
 * For YouTube the answer may instead be a `handle` (`@the_band`, as typed or
 * from a youtube.com/@the_band URL): it passed the shape check and is sent as
 * `channel_id` like a value, but it is not what the backend stores - the
 * server resolves it to the channel ID (see the file comment), so the caller
 * should re-read the source afterwards rather than assume it stored the
 * handle. Channel IDs come back as `value` directly.
 *
 * `error` is worded in the locale of `t` (English without one).
 * @returns {{ value: string } | { handle: string } | { error: string }}
 */
export const normalizeChannelId = (source, raw, t = englishT) => {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return { error: t('streamSource.errors.channelRequired') };

  if (source === 'twitch') {
    const path = urlPath(trimmed, 'twitch.tv');
    const login = path === null ? trimmed : path.split('/')[0];
    return TWITCH_LOGIN_PATTERN.test(login) ? { value: login } : { error: t('streamSource.errors.twitchChannel') };
  }

  if (source === 'youtube') {
    const shapeError = { error: t('streamSource.errors.youtubeChannel') };
    const path = urlPath(trimmed, 'youtube.com');
    // Bare input is an ID or an @handle. Of the URL forms only /channel/<id>
    // carries a channel ID and only /@handle a handle; /c/<name> and
    // /user/<name> are legacy custom URLs nothing here can resolve.
    let candidate = trimmed;
    let underChannelPath = false;
    if (path !== null) {
      const [first, second = ''] = path.split('/');
      underChannelPath = first === 'channel';
      candidate = underChannelPath ? second : first;
    }
    if (candidate.startsWith('@')) {
      // /channel/@x is not a URL YouTube has; only a bare or top-level handle counts.
      if (underChannelPath || !YOUTUBE_HANDLE_PATTERN.test(candidate.slice(1))) return shapeError;
      return { handle: candidate };
    }
    return YOUTUBE_CHANNEL_ID_PATTERN.test(candidate) ? { value: candidate } : shapeError;
  }

  return { error: t('streamSource.errors.chooseProvider') };
};

/**
 * The server's own words for a failure: fetchWrapper's `message` is the
 * response body when there was one to show, and the status text otherwise.
 * Ends with a full stop like the messages written here, as the backend's
 * error strings do not carry one.
 */
const serverWords = (err) => {
  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  if (!message || message === err.statusText) return '';
  return /[.!?]$/.test(message) ? message : `${message}.`;
};

/**
 * What to tell the band member when `PUT /bands/{id}/stream/source` fails
 * with `err` (fetchWrapper's `{ status, statusText, message }`), given the
 * `handle` that was sent as channel_id, if one was.
 *
 * The channel was already validated client-side against the same rules the
 * backend applies, so with a channel ID or Twitch login a rejection means
 * either the server's own message (a 400 carries one for a missing/oversized
 * channel or unknown source), an outage, or - should the two validators ever
 * drift - a channel the server refused. The server's words are passed on
 * when they came through; the fallback wording has to fit all three.
 *
 * With a handle (see the file comment) 400 and 429 each mean one thing -
 * YouTube has no such channel; the member's lookup budget - and the wording
 * here names the handle and says where to find the ID, so it is preferred
 * to the server's. A 503 means one of two things and only the server's body
 * says which, and whether retrying can help, so its words are shown when
 * they came through. Every handle failure says to paste the channel ID
 * instead, which needs no lookup and is always accepted.
 *
 * Messages written here come out in the locale of `t` (English without one);
 * the server's words are shown as they came (ENG-386).
 */
export const streamSourceSaveError = (err, handle = null, t = englishT) => {
  const status = err?.status;
  if (handle) {
    if (status === 400) return youtubeHandleNotFoundError(handle, t);
    if (status === 429) return t('streamSource.errors.handleLookupLimit');
    if (status === 503) return serverWords(err) || t('streamSource.errors.handleLookupUnavailable');
    return t('streamSource.errors.handleSaveFailed');
  }
  if (status === 400) {
    const detail = serverWords(err);
    return detail
      ? t('streamSource.errors.channelRejectedWithDetail', { detail })
      : t('streamSource.errors.channelRejected');
  }
  return t('streamSource.errors.saveFailed');
};

/**
 * Public page for the configured channel, shown next to the stored
 * identifier. The backend stores channel IDs only, but the panel falls back to
 * what it sent when the confirming re-read fails, which for YouTube may be the
 * @handle; that has a page of its own.
 */
export const channelPageUrl = (source, channelId) => {
  if (!channelId) return null;
  if (source === 'twitch') return `https://www.twitch.tv/${encodeURIComponent(channelId)}`;
  if (source === 'youtube') {
    return channelId.startsWith('@')
      ? `https://www.youtube.com/@${encodeURIComponent(channelId.slice(1))}`
      : `https://www.youtube.com/channel/${encodeURIComponent(channelId)}`;
  }
  return null;
};

/** Normalise the `{ source, channel_id }` API shape; null when the band has no source. */
export const normalizeStreamSource = (data) => {
  const source = data?.source ?? data?.Source;
  if (!source) return null;
  return { source, channelId: data.channel_id ?? data.channelId ?? data.ChannelID ?? '' };
};
